/* ARCK bulky freight surcharge — CART-SIDE RECONCILER (loads on every page)
 *
 * Why this exists
 * ---------------
 * The old design added the "Bulky Freight Surcharge" line item from the PRODUCT
 * PAGE only (arck-product-bulky.js intercepted the add-to-cart form there).
 * That missed every other way a customer can add a bulky item — collection
 * grids, search results, quick view, the cart drawer, "buy again" links — so in
 * practice almost no order ever carried the surcharge, and the cart-page toggle
 * then defaulted to "Free Pickup" because it saw no surcharge line.
 *
 * This script instead reconciles the surcharge against the CART, wherever the
 * items came from:
 *
 *   desired surcharge qty = total qty of bulky-tagged items in the cart
 *                           (0 if the customer chose Free Pickup)
 *
 * It runs:
 *   1. on every page load,
 *   2. as soon as the theme completes any /cart/add|change|update|clear request,
 *   3. immediately before checkout — a capture-phase guard on the checkout
 *      controls that fixes the cart first, then replays the original click.
 *
 * Making the customer SEE it
 * --------------------------
 * Halo re-renders the side cart with `GET /cart?view=ajax_side_cart` and repaints
 * the header count from `GET /cart.js`, and it fires both the instant the add
 * returns — i.e. before our surcharge line exists. The cart was right but the
 * drawer and the badge showed only the product, which reads to the customer (and
 * to us) as "the surcharge isn't being added". So while a reconcile is in flight
 * we HOLD the theme's cart reads until it finishes; the theme then renders the
 * real cart, surcharge included. Our own requests use the pre-patch fetch and are
 * never held, so there is no way to deadlock.
 *
 * The customer's Pickup/Delivery choice lives in cart attribute
 * "Delivery method" and is set by arck-cart-delivery-toggle.js on the cart page.
 */
(function () {
  'use strict';

  var SURCHARGE_VARIANT_ID = 43124952498237;   // "Bulky Freight Surcharge", SKU 11999, $20
  var BULKY_TAG            = 'bulky';
  var ATTR_KEY             = 'Delivery method';
  var ATTR_PICKUP          = 'Free Pickup (Parramatta)';
  var ATTR_DELIVERY        = 'Delivery';
  var PROMPTED_KEY         = 'arck_bulky_choice_prompted';
  var CACHE_KEY            = 'arck_bulky_tag_cache_v1';
  var MAX_FAILURES         = 2;                // stop retrying if the cart API keeps refusing
  var CHECKOUT_TIMEOUT_MS  = 4000;             // never block checkout longer than this
  var HOLD_TIMEOUT_MS      = 3000;             // never hold a theme cart read longer than this

  // Captured BEFORE we patch window.fetch, so every internal request bypasses
  // both the write-watcher and the read-hold.
  var nativeFetch = window.fetch.bind(window);

  var failures = 0;
  var pending  = null;   // promise for a reconcile triggered by a theme cart write

  var api = window.ArckBulky = window.ArckBulky || {};

  /* ---------- bulky-tag lookup (per-handle, cached for the session) ---------- */

  function readCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function writeCache(cache) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* private mode */ }
  }

  function isBulkyHandle(handle) {
    var cache = readCache();
    if (Object.prototype.hasOwnProperty.call(cache, handle)) {
      return Promise.resolve(!!cache[handle]);
    }
    return nativeFetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var bulky = !!(p && Array.isArray(p.tags) && p.tags.indexOf(BULKY_TAG) !== -1);
        var c = readCache();
        c[handle] = bulky ? 1 : 0;
        writeCache(c);
        return bulky;
      })
      .catch(function () { return false; });   // unknown → never charge
  }

  /* ---------- cart helpers ---------- */

  function isSurchargeLine(item) {
    return !!item && (item.variant_id === SURCHARGE_VARIANT_ID || item.id === SURCHARGE_VARIANT_ID);
  }

  function getCart() {
    return nativeFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  function cartPost(path, body) {
    return nativeFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error(path + ' failed: ' + res.status);
      return res.json();
    });
  }

  function currentSurchargeQty(cart) {
    return (cart.items || []).reduce(function (sum, it) {
      return isSurchargeLine(it) ? sum + (it.quantity || 0) : sum;
    }, 0);
  }

  function desiredSurchargeQty(cart) {
    if (cart.attributes && cart.attributes[ATTR_KEY] === ATTR_PICKUP) {
      return Promise.resolve(0);
    }
    var lines = (cart.items || []).filter(function (it) { return !isSurchargeLine(it); });
    if (!lines.length) return Promise.resolve(0);

    var byHandle = {};
    lines.forEach(function (it) {
      byHandle[it.handle] = (byHandle[it.handle] || 0) + (it.quantity || 0);
    });
    var handles = Object.keys(byHandle);
    return Promise.all(handles.map(isBulkyHandle)).then(function (flags) {
      return handles.reduce(function (sum, h, i) {
        return flags[i] ? sum + byHandle[h] : sum;
      }, 0);
    });
  }

  /**
   * True when the cart holds bulky items and the customer has not yet said
   * whether this order is delivery or pickup. Shopify's checkout offers Pickup
   * on a page no theme script can touch, and the line items are frozen by then
   * (order #6415, 2026-09-15, paid $40 freight on a counter pickup) — so the
   * question has to be answered on the cart page, before checkout opens.
   */
  function needsDeliveryChoice() {
    return getCart().then(function (cart) {
      if (!cart) return false;
      var choice = cart.attributes && cart.attributes[ATTR_KEY];
      if (choice === ATTR_PICKUP || choice === ATTR_DELIVERY) return false;
      return desiredSurchargeQtyIgnoringChoice(cart).then(function (qty) { return qty > 0; });
    }).catch(function () { return false; });   // never block checkout on an error
  }

  function desiredSurchargeQtyIgnoringChoice(cart) {
    var lines = (cart.items || []).filter(function (it) { return !isSurchargeLine(it); });
    if (!lines.length) return Promise.resolve(0);
    var byHandle = {};
    lines.forEach(function (it) {
      byHandle[it.handle] = (byHandle[it.handle] || 0) + (it.quantity || 0);
    });
    var handles = Object.keys(byHandle);
    return Promise.all(handles.map(isBulkyHandle)).then(function (flags) {
      return handles.reduce(function (sum, h, i) { return flags[i] ? sum + byHandle[h] : sum; }, 0);
    });
  }

  function alreadyPrompted() {
    try { return sessionStorage.getItem(PROMPTED_KEY) === '1'; } catch (e) { return false; }
  }

  function markPrompted() {
    try { sessionStorage.setItem(PROMPTED_KEY, '1'); } catch (e) { /* private mode */ }
  }

  /* ---------- the reconcile ---------- */

  var queue = Promise.resolve();

  /**
   * Bring the surcharge line into line with the cart.
   * Resolves { changed, desired, current }. Never rejects — a broken surcharge
   * product must not break the cart.
   *
   * Calls are serialised rather than dropped: one arriving while another is in
   * flight (the delivery toggle firing during the write-watcher's reconcile,
   * say) must still run, or the surcharge silently lags a step behind.
   */
  function reconcile(opts) {
    var run = function () { return doReconcile(opts); };
    queue = queue.then(run, run);
    return queue;
  }

  function doReconcile(opts) {
    var silent = !!(opts && opts.silent);   // don't re-render the cart page (used by the checkout guard)
    if (failures >= MAX_FAILURES) {
      return Promise.resolve({ changed: false, skipped: true });
    }

    return getCart()
      .then(function (cart) {
        if (!cart) return { changed: false };
        var current = currentSurchargeQty(cart);
        return desiredSurchargeQty(cart).then(function (desired) {
          if (desired === current) return { changed: false, desired: desired, current: current };

          var work;
          if (current === 0) {
            // /cart/update.js cannot introduce a new line — add it.
            work = cartPost('/cart/add.js', { id: SURCHARGE_VARIANT_ID, quantity: desired });
          } else {
            // Set (or clear, when desired === 0) the existing line.
            work = cartPost('/cart/change.js', { id: String(SURCHARGE_VARIANT_ID), quantity: desired });
          }
          return work.then(function (updated) {
            return { changed: true, desired: desired, current: current, cart: updated };
          });
        });
      })
      .catch(function (err) {
        failures++;
        if (window.console && window.console.warn) {
          console.warn('[arck-bulky-surcharge] reconcile failed', err);
        }
        return { changed: false, error: true };
      })
      .then(function (result) {
        if (result && result.changed) notifyThemeCartChanged(silent);
        return result;
      });
  }

  api.reconcile = reconcile;
  api.SURCHARGE_VARIANT_ID = SURCHARGE_VARIANT_ID;

  /* ---------- keep the theme's cart UI honest after we change things ---------- */

  function paintCartCount(count) {
    // Belt and braces for the header bubble, in case the theme repainted it from
    // a cart read that finished before our line existed.
    var sels = ['.cartCount', '[data-cart-count]', '.cart-count-bubble .text'];
    sels.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (/^\s*\d+\s*$/.test(el.textContent)) el.textContent = String(count);
      });
    });
  }

  function notifyThemeCartChanged(silent) {
    getCart().then(function (cart) { if (cart) paintCartCount(cart.item_count); });

    ['cart:refresh', 'cart:build', 'theme:cart:reload'].forEach(function (name) {
      try { document.dispatchEvent(new CustomEvent(name, { bubbles: true })); } catch (e) { /* noop */ }
    });

    // On the cart page the line items are server-rendered, so re-render once.
    // Safe against loops: after the reload, desired === current, nothing changes.
    if (!silent && /^\/cart\/?$/.test(location.pathname)) {
      var sep = location.search ? '&' : '?';
      location.replace(location.pathname + location.search + sep + 'sc=' + Date.now());
    }
  }

  /* ---------- watching the theme's cart traffic ---------- */

  function isCartWrite(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  }

  // The reads the theme uses to repaint the drawer and the header count.
  function isCartRead(url) {
    var u = String(url || '');
    if (/\/cart\.js(\?|$)/.test(u)) return true;
    return /\/cart\b/.test(u) && /(view=|section_id=|sections=)/.test(u);
  }

  function notePending(p) {
    pending = p;
    var clear = function () { if (pending === p) pending = null; };
    p.then(clear, clear);
    return p;
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve({ timedOut: true }); } }, ms);
      promise.then(function (v) { if (!done) { done = true; clearTimeout(t); resolve(v); } },
                   function () { if (!done) { done = true; clearTimeout(t); resolve({ error: true }); } });
    });
  }

  // Hold a theme cart read until the reconcile it is racing has finished.
  function waitForPending() {
    return pending ? withTimeout(pending, HOLD_TIMEOUT_MS) : Promise.resolve();
  }

  function watchCartRequests() {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (input && input.url) ? input.url : input;

      if (isCartWrite(url)) {
        var p = origFetch.apply(this, arguments);
        p.then(function () { notePending(reconcile({ silent: true })); }, function () { /* ignore */ });
        return p;
      }

      if (isCartRead(url) && pending) {
        var self = this, args = arguments;
        return waitForPending().then(function () { return origFetch.apply(self, args); });
      }

      return origFetch.apply(this, arguments);
    };

    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__arckWrite = isCartWrite(url);
      this.__arckRead  = isCartRead(url);
      if (this.__arckWrite) {
        this.addEventListener('load', function () { notePending(reconcile({ silent: true })); });
      }
      return origOpen.apply(this, arguments);
    };

    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var self = this, args = arguments;
      if (this.__arckRead && pending) {
        waitForPending().then(function () { origSend.apply(self, args); });
        return;
      }
      return origSend.apply(this, args);
    };
  }

  /* ---------- the checkout guard ---------- */

  // Halo/Ella renders the checkout controls as <button type="submit" class="button-checkout">
  // with ids #cart-checkout (cart page) and #cart-sidebar-checkout (cart drawer) —
  // no name="checkout", so match on those too.
  var CHECKOUT_SELECTOR = [
    '.button-checkout',
    '#cart-checkout',
    '#cart-sidebar-checkout',
    '[name="checkout"]',
    'a[href*="/checkout"]',
    '.cart__checkout',
    '.btn-proceed-checkout',
    '.cart-drawer__checkout'
  ].join(',');

  function guardCheckout(e) {
    var el = e.target && e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
    if (!el) return;
    if (el.getAttribute('data-arck-surcharge-ok') === '1') return;   // replayed click — let it through

    // Hold the click just long enough to fix the cart, then replay it so the
    // theme's own handler (cart note, attributes, form submit) still runs.
    e.preventDefault();
    e.stopImmediatePropagation();

    var onCartPage = /^\/cart\/?$/.test(location.pathname);

    withTimeout(reconcile({ silent: true }), CHECKOUT_TIMEOUT_MS)
      .then(function () {
        // Checking out from the drawer skips the cart page, and with it the
        // delivery/pickup question. Send them there once to answer it.
        if (onCartPage || alreadyPrompted()) return false;
        return withTimeout(needsDeliveryChoice(), CHECKOUT_TIMEOUT_MS).then(function (needs) {
          return needs === true;
        });
      })
      .then(function (divert) {
        if (divert) {
          markPrompted();   // one diversion only — a blocked checkout is a lost sale
          location.href = '/cart?choose=1';
          return;
        }
        el.setAttribute('data-arck-surcharge-ok', '1');
        if (el.isConnected) el.click();
        setTimeout(function () { el.removeAttribute('data-arck-surcharge-ok'); }, 5000);
      });
  }

  /* ---------- boot ---------- */

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  watchCartRequests();
  document.addEventListener('click', guardCheckout, true);
  onReady(function () { reconcile(); });
})();
